import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Typography, Space, Alert } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface TVPreviewProps {
    className?: string;
}

export const TVPreview: React.FC<TVPreviewProps> = ({ className }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [refreshCounter, setRefreshCounter] = useState(0);
    const iframeRef = useRef<HTMLIFrameElement>(null);


    const handleRefresh = () => {
        setIsLoading(true);
        setHasError(false);
        setLastRefresh(new Date());
        
        // Force React to re-mount iframe by changing key (more declarative approach)
        setRefreshCounter(prev => prev + 1);
    };

    const handleIframeLoad = () => {
        setIsLoading(false);
        setHasError(false);
    };

    const handleIframeError = () => {
        setIsLoading(false);
        setHasError(true);
    };

    // Timeout-based error handling for better robustness
    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        // When loading starts, set a timeout to detect failed loads
        if (isLoading) {
            timeoutId = setTimeout(() => {
                // If timeout fires, assume loading failed
                handleIframeError();
            }, 15000); // 15-second timeout
        }

        // Cleanup timeout when loading finishes or component unmounts
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isLoading]);

    // Auto-refresh every 5 minutes
    useEffect(() => {
        const refreshTimer = setInterval(() => {
            handleRefresh();
        }, 5 * 60 * 1000);

        return () => clearInterval(refreshTimer);
    }, []);

    const renderContent = () => {
        if (hasError) {
            return (
                <div style={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px'
                }}>
                    <div style={{ 
                        height: '60vh',
                        width: `${60 * 16/9}vh`, // 16:9 aspect ratio based on height
                        maxWidth: '95vw', // Fallback for very wide screens
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                        borderRadius: '8px',
                        color: 'white',
                        textAlign: 'center',
                        padding: '40px'
                    }}>
                        <Alert
                            message="TV Preview Unavailable"
                            description={
                                <div>
                                    <p>The Flutter TV app is not running or accessible.</p>
                                    <p><strong>Development:</strong> Run <code>npm run dev:flutter</code> in a separate terminal</p>
                                    <p><strong>Production:</strong> Flutter web build will be included automatically</p>
                                </div>
                            }
                            type="warning"
                            showIcon
                            style={{ marginBottom: '20px' }}
                        />
                        <Button 
                            type="primary" 
                            icon={<ReloadOutlined />}
                            onClick={handleRefresh}
                        >
                            Retry
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div style={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
            }}>
                <div style={{ 
                    position: 'relative',
                    height: '70vh', // Use 70% of viewport height
                    width: `${70 * 16/9}vh`, // Calculate width to maintain 16:9 aspect ratio
                    maxWidth: '95vw', // Fallback for very wide screens
                }}>
                    {isLoading && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                            borderRadius: '8px',
                            zIndex: 10
                        }}>
                            <div style={{ textAlign: 'center', color: 'white' }}>
                                <div style={{ fontSize: '24px', marginBottom: '16px' }}>🕌</div>
                                <Text style={{ color: 'white' }}>Loading TV Preview...</Text>
                            </div>
                        </div>
                    )}
                    
                    <iframe
                        key={refreshCounter}
                        ref={iframeRef}
                        src={process.env.NODE_ENV === 'development' ? '/tv-preview/?preview=true' : '/tv-preview/?preview=true'}
                        width="100%"
                        height="100%"
                        style={{ 
                            border: 'none', 
                            borderRadius: '8px',
                            backgroundColor: '#1e3c72', // Fallback while loading
                            display: isLoading ? 'none' : 'block'
                        }}
                        title="TV App Preview"
                        onLoad={handleIframeLoad}
                        onError={handleIframeError}
                    />
                </div>
            </div>
        );
    };

    return (
        <Card 
            className={className}
            title={
                <Space>
                    <Title level={4} style={{ margin: 0 }}>TV App Preview</Title>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        Last updated: {lastRefresh.toLocaleTimeString()}
                    </Text>
                </Space>
            }
            extra={
                <Space>
                    <Button 
                        icon={<ReloadOutlined />}
                        onClick={handleRefresh}
                        size="small"
                    >
                        Refresh
                    </Button>
                    
                </Space>
            }
        >
            {renderContent()}
            
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    This preview shows the actual Flutter TV app running in your browser.
                    The layout and functionality are identical to what displays on TV screens.
                    To show the settings screen, press Ctrl+` (backquote key).
                </Text>
            </div>
        </Card>
    );
}; 